/**
 * The Tenancies + leases CSV import columns (L-12; plans/L-12.md; ADR-76).
 *
 * ONE ROW = one tenancy (an existing organization at an existing property),
 * optionally its lease, and optionally that lease's SharePoint folder link —
 * the grain of the Yardi export. Every rule is the shared create schemas'
 * (`tenancyCreateSchema`, `leaseWriteSchema`, `documentWriteSchema`); the
 * import turns text into their types and resolves names to ids, and adds no
 * second validator. Matt's decisions, 2026-10-12: the org by its EXACT name
 * (more than one is an error), the property by its Yardi ID, the two status
 * columns as create attributes (ADR-74 amended by ADR-76), dates as ISO or
 * M/D/YYYY, the Yardi tenant ID written in the create, Unit naming an EXISTING
 * space only.
 *
 * NOT COLUMNS: rent and the rest of the lease abstract (docs/18 X-REVISIONS),
 * lease contacts (3C.4), creating spaces or organizations, and inactive or
 * superseded history (update-by-import, L-33).
 */

import type { ImportColumn } from './import-runs.ts';

export type TenancyImportField =
  | 'tenantOrganization'
  | 'propertyYardiId'
  | 'yardiTenantId'
  | 'yardiTenantCode'
  | 'tenancyStatus'
  | 'moveIn'
  | 'moveOut'
  | 'unit'
  | 'yardiSharePointUrl'
  | 'propertyManagementUrl'
  | 'tenancyNotes'
  | 'leaseStatus'
  | 'commencement'
  | 'expiration'
  | 'executedDate'
  | 'rentableSqFt'
  | 'leaseNotes'
  | 'leaseFolderUrl';

const DATE = 'a date, as 2026-10-12 or 10/12/2026';

/** The eighteen template columns, in template order (plans/L-12.md, approved). */
export const TENANCY_IMPORT_COLUMNS: readonly ImportColumn<TenancyImportField>[] = [
  {
    header: 'Tenant Organization',
    field: 'tenantOrganization',
    required: true,
    rule: 'the exact name of an active tenant organization',
    example: '',
    messages: {
      reference_not_found:
        'No tenant organization has exactly this name — create it first (Tenant organizations import)',
      reference_inactive: 'This tenant organization is inactive — reactivate it first',
      reference_ambiguous:
        'More than one tenant organization has exactly this name — rename one, then import',
    },
  },
  {
    header: 'Property Yardi ID',
    field: 'propertyYardiId',
    required: true,
    rule: 'the Yardi ID of an active property',
    example: 'ard01',
    messages: {
      reference_not_found: 'No property in PropOS has this Yardi ID',
      reference_inactive: 'This property is inactive — a tenancy needs an active property',
    },
  },
  {
    header: 'Yardi Tenant ID',
    field: 'yardiTenantId',
    required: false,
    rule: 'unique across all tenancies',
    example: 't0001234',
  },
  {
    header: 'Yardi Tenant Code',
    field: 'yardiTenantCode',
    required: false,
    rule: 'free text',
    example: 'qfg01',
  },
  {
    header: 'Tenancy Status',
    field: 'tenancyStatus',
    required: false,
    rule: 'Pending or Active; blank is Pending',
    example: 'Active',
  },
  { header: 'Move-in Date', field: 'moveIn', required: false, rule: DATE, example: '3/1/2024' },
  { header: 'Move-out Date', field: 'moveOut', required: false, rule: DATE, example: '' },
  {
    header: 'Unit',
    field: 'unit',
    required: false,
    rule: 'an existing space at the property, by its Yardi unit ID or its label',
    example: '100',
    messages: {
      reference_not_found: 'This property has no space with this unit ID or label',
      reference_ambiguous: 'More than one space at this property matches this unit',
    },
  },
  {
    header: 'Yardi SharePoint URL',
    field: 'yardiSharePointUrl',
    required: false,
    rule: 'a full web address, including https://',
    example: '',
  },
  {
    header: 'Property Management URL',
    field: 'propertyManagementUrl',
    required: false,
    rule: 'a full web address, including https://',
    example: '',
  },
  {
    header: 'Tenancy Notes',
    field: 'tenancyNotes',
    required: false,
    rule: 'up to 4,000 characters',
    example: '',
  },
  {
    header: 'Lease Status',
    field: 'leaseStatus',
    required: false,
    rule: 'Draft or Active; blank with no lease column means no lease',
    example: 'Active',
  },
  {
    header: 'Commencement Date',
    field: 'commencement',
    required: false,
    rule: `${DATE}; required for an active lease`,
    example: '3/1/2024',
    messages: { required_missing: 'An active lease needs a Commencement Date' },
  },
  {
    header: 'Expiration Date',
    field: 'expiration',
    required: false,
    rule: `${DATE}, after the commencement; required for an active lease`,
    example: '2/28/2029',
    messages: { required_missing: 'An active lease needs an Expiration Date' },
  },
  {
    header: 'Executed Date',
    field: 'executedDate',
    required: false,
    rule: DATE,
    example: '',
  },
  {
    header: 'Rentable Sq Ft',
    field: 'rentableSqFt',
    required: false,
    rule: 'a whole number from 1 to 10,000,000',
    example: '4200',
  },
  {
    header: 'Lease Notes',
    field: 'leaseNotes',
    required: false,
    rule: 'up to 4,000 characters',
    example: '',
  },
  {
    header: 'Lease Folder URL',
    field: 'leaseFolderUrl',
    required: false,
    rule: "the lease's SharePoint folder, a full web address",
    example: '',
  },
];
